import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getExercises, exercisesBase, isHomeFriendly, type ResolvedExercise } from '../lib/exercises';
import { SITE_URL, getStrings, altLinks, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { Thumb } from '../components/Thumb';
import { workoutStrings, toolPath } from '../lib/tools';

type Category = ResolvedExercise['category'];
type Difficulty = ResolvedExercise['difficulty'];

export default function ExercisesIndex({ lang = 'en' as Lang }: { lang?: Lang }) {
  const c = getStrings(lang).exercisesChrome;
  const base = exercisesBase(lang);
  const all = getExercises(lang);
  const [params] = useSearchParams();

  const [category, setCategory] = useState<Category | 'all'>('all');
  const [difficulty, setDifficulty] = useState<Difficulty | 'all'>('all');
  const [group, setGroup] = useState<string>(params.get('group') || 'all');
  const [homeOnly, setHomeOnly] = useState(params.get('home') === '1');
  const technique = params.get('technique');

  const groups = useMemo(() => Array.from(new Set(all.map((e) => e.armGroup).filter(Boolean))) as string[], [all]);

  const filtered = all.filter((e) => {
    if (category !== 'all' && e.category !== category) return false;
    if (difficulty !== 'all' && e.difficulty !== difficulty) return false;
    if (group !== 'all' && e.armGroup !== group) return false;
    if (technique && !e.relatedTechniqueIds.includes(technique)) return false;
    if (homeOnly && !isHomeFriendly(e.equipmentKeys)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={c.indexMetaTitle}
        description={c.indexMetaDescription}
        canonical={`${SITE_URL}${base}`}
        lang={lang}
        alternates={altLinks('/armwrestling/exercises')}
      />
      <BlogHeader lang={lang} />

      <main className="container mx-auto px-6 py-16 max-w-5xl">
        <h1 className="text-4xl md:text-5xl font-extrabold text-ink mb-4">{c.indexHeading}</h1>
        <p className="text-muted text-lg mb-8 max-w-2xl">{c.indexTagline}</p>

        <a
          href={toolPath(lang, 'armwrestling-workout-generator')}
          className="card-chunky is-hoverable flex flex-col sm:flex-row sm:items-center gap-4 p-5 mb-10"
        >
          <span className="chip-icon w-12 h-12 text-2xl flex-shrink-0" aria-hidden="true">🗓️</span>
          <span className="flex-1 min-w-0">
            <span className="block font-heading font-black text-ink text-lg">{workoutStrings(lang).callout.title}</span>
            <span className="block text-muted text-sm">{workoutStrings(lang).callout.body}</span>
          </span>
          <span className="btn-3d btn-3d--sm px-4 py-2 flex-shrink-0">{workoutStrings(lang).callout.cta}</span>
        </a>

        <div className="flex flex-wrap gap-4 mb-10">
          <FilterGroup
            label={c.filterCategoryLabel}
            value={category}
            onChange={(v) => setCategory(v as Category | 'all')}
            options={[
              { value: 'all', label: c.filterAll },
              { value: 'dynamic', label: c.category.dynamic },
              { value: 'static', label: c.category.static },
              { value: 'warmup', label: c.category.warmup },
            ]}
          />
          <FilterGroup
            label={c.filterDifficultyLabel}
            value={difficulty}
            onChange={(v) => setDifficulty(v as Difficulty | 'all')}
            options={[
              { value: 'all', label: c.filterAll },
              { value: 'beginner', label: c.difficulty.beginner },
              { value: 'intermediate', label: c.difficulty.intermediate },
              { value: 'advanced', label: c.difficulty.advanced },
            ]}
          />
          <FilterGroup
            label={c.filterGroupLabel}
            value={group}
            onChange={setGroup}
            options={[
              { value: 'all', label: c.filterAll },
              ...groups.map((g) => ({ value: g, label: c.armGroup[g as keyof typeof c.armGroup] || g })),
            ]}
          />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={homeOnly}
              onChange={(e) => setHomeOnly(e.target.checked)}
              className="w-4 h-4 rounded border-line bg-surface accent-primary"
            />
            <span className="text-faint font-medium">{c.homeOnlyLabel}</span>
          </label>
        </div>

        {filtered.length === 0 ? (
          <p className="text-faint">{c.noResults}</p>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((e) => (
              <li key={e.slug}>
                <Link
                  to={`${base}/${e.slug}`}
                  className="group block h-full overflow-hidden rounded-2xl border border-line bg-surface/60 hover:border-primary/40 transition"
                >
                  <div className="relative">
                    <Thumb
                      src={e.imageUrl}
                      alt={e.t.name}
                      emoji={e.emoji}
                      className="aspect-[4/3] w-full"
                      imgClassName="object-top"
                    />
                    <span className="absolute top-2 right-2 rounded-full bg-surface/90 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-ink uppercase tracking-wide shadow-sm">
                      {c.difficulty[e.difficulty]}
                    </span>
                  </div>
                  <div className="p-4">
                    <h2 className="text-ink font-bold group-hover:text-primary transition mb-1">{e.t.name}</h2>
                    <p className="text-muted text-sm line-clamp-2">{e.t.muscles}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>

      <Footer lang={lang} />
    </div>
  );
}

function FilterGroup<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-faint font-medium">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="bg-surface border border-line text-ink rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-primary/50"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
